import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ReportColourMatrix } from "../components/ReportColourMatrix";
import { ShieldCheck, CreditCard, MapPinned, Wrench, Undo2, Phone, FileCheck2, Globe } from "lucide-react";

const ELIGIBILITY = [
  ["UAE residents", "Emirates ID · valid UAE driving licence · payment card in the hirer's name"],
  ["Tourists & visitors", "Passport · UAE entry stamp/visa · home-country licence (IDP for some nationalities) · payment card in your name"],
  ["GCC nationals", "GCC licence accepted · passport copy also required · payment card in your name"],
  ["Age & licence", "Minimum 21 (standard) / 25 (premium) · licence held for at least 12 months · no upper age limit with a valid licence"],
];

const LICENCE_COUNTRIES = [
  ["GCC", "Saudi Arabia · Kuwait · Bahrain · Oman · Qatar"],
  ["Europe", "United Kingdom · Ireland · Germany · France · Italy · Spain · Netherlands · Belgium · Austria · Switzerland · Sweden · Norway · Denmark · Finland · Poland · Portugal · Greece · Romania · Iceland · Luxembourg · Türkiye"],
  ["North America", "United States · Canada"],
  ["Asia & Pacific", "Japan · South Korea · China · Hong Kong · Singapore · Australia · New Zealand"],
  ["Africa", "South Africa"],
];

const ACCIDENT_ROUTING = [
  ["Dubai", "Dubai Police app · 901 for non-emergency support"],
  ["Abu Dhabi", "Saaed Smart app or the Abu Dhabi Police app"],
  ["Sharjah", "Rafid 80092 or the Rafid Smart app"],
  ["Ajman", "Saaed 800 72233, the Saaed Smart app, or the MOI UAE service"],
  ["Umm Al Quwain", "MOI UAE service or Saaed 800 72233"],
  ["Ras Al Khaimah", "MOI UAE service, or Saaed where availability is confirmed"],
  ["Fujairah", "MOI UAE service; Saaed only where availability is confirmed"],
];

const ACCIDENT_INFO = [
  "Driving licences for all involved drivers.",
  "Mulkiya (vehicle registration) and vehicle plate numbers.",
  "Emirates ID where requested.",
  "Exact location and an active contact number.",
  "Close-up photos of the damage and plates, plus wide shots of the scene.",
  "The final official report, needed for Europcar and insurance handling.",
];

const Section = ({ icon: Icon, title, children, testId }) => (
  <section className="bg-white rounded-2xl border border-[#E2E8F0] p-6" data-testid={testId}>
    <h2 className="heading-upper text-lg text-[#144733] flex items-center gap-2 mb-4"><Icon className="w-5 h-5 text-[#009900]" /> {title}</h2>
    {children}
  </section>
);

const Li = ({ children }) => <li className="flex gap-2 text-sm text-[#57685e] leading-relaxed"><span className="text-[#009900] font-bold shrink-0">·</span><span>{children}</span></li>;

export default function RentersGuide() {
  return (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />
      <div className="bg-[#144733]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 promo-stripe">
          <h1 className="heading-upper text-2xl sm:text-3xl text-white" data-testid="guide-heading">The Renter's Guide</h1>
          <p className="text-white/80 text-sm mt-2 max-w-2xl">Everything worth knowing before, during and after your Europcar rental, deposits, documents, tolls, accidents and returns, in plain words.</p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 w-full flex-1 space-y-6">
        <Section icon={FileCheck2} title="Before you book, who can rent" testId="guide-eligibility">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[480px]">
              <tbody>
                {ELIGIBILITY.map(([who, what]) => (
                  <tr key={who} className="border-t border-[#f1f3f1]">
                    <td className="py-3 pr-4 text-xs font-semibold uppercase tracking-wide text-[#144733] align-top w-44">{who}</td>
                    <td className="py-3 text-[#57685e]">{what}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section icon={Globe} title="Tourist licence eligibility, driving on your home licence" testId="guide-tourist-licences">
          <p className="text-sm text-[#57685e] mb-4">Visitors from the countries below may drive in the UAE on their valid home-country driving licence for the duration of their visit (passport and UAE entry stamp/visa still required). If your country isn't listed, bring an International Driving Permit (IDP) alongside your home licence.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[#144733]">
                  <th className="py-2 pr-4 font-semibold w-40">Region</th>
                  <th className="py-2 font-semibold">Home licence accepted (no IDP required)</th>
                </tr>
              </thead>
              <tbody>
                {LICENCE_COUNTRIES.map(([region, countries]) => (
                  <tr key={region} className="border-t border-[#f1f3f1]">
                    <td className="py-3 pr-4 text-xs font-semibold uppercase tracking-wide text-[#144733] align-top">{region}</td>
                    <td className="py-3 text-[#57685e]">{countries}</td>
                  </tr>
                ))}
                <tr className="border-t border-[#f1f3f1] bg-[#f4f7f4]">
                  <td className="py-3 pr-4 text-xs font-semibold uppercase tracking-wide text-[#144733] align-top">All other countries</td>
                  <td className="py-3 text-[#57685e]">International Driving Permit (IDP) required together with your valid home-country licence.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[#57685e] mt-3">This list is indicative and set by UAE authorities, it can change. If you're unsure, carry an IDP to be safe, or message us on WhatsApp before you travel and we'll confirm for your nationality.</p>
        </Section>

        <Section icon={CreditCard} title="Deposits & security holds" testId="guide-deposits">
          <ul className="space-y-2">
            <Li><strong>Credit card:</strong> a pre-authorisation ("AUTH"), nothing is charged. AED 1,500 (standard) / AED 2,500 (premium), released automatically by your bank 21 to 28 days after return.</Li>
            <Li><strong>Debit card:</strong> a charged, refundable deposit. AED 2,000 (standard) / AED 3,000 (premium), refunded 28 to 30 days after closure plus 3 to 5 bank days.</Li>
            <Li><strong>Got a "charged" SMS for the hold?</strong> On a credit card it's only a pre-authorisation, your slip shows "AUTH" and nothing was charged.</Li>
            <Li><strong>No-Deposit Waiver:</strong> skip the hold or deposit entirely, standard cars AED 35/day, 160/week or 210/month; premium AED 50/day, 260/week or 410/month (+ VAT). Nothing blocked on your card.</Li>
          </ul>
        </Section>

        <Section icon={MapPinned} title="On the road" testId="guide-ontheroad">
          <ul className="space-y-2">
            <Li><strong>UAE only:</strong> rental vehicles may not be driven into Oman, Saudi Arabia, Qatar or any other country or territory.</Li>
            <Li><strong>Mileage:</strong> daily & weekly rentals include unlimited km within the UAE; monthly includes 3,000 km (4,000/5,000 km upgrades). Excess km AED 0.50/0.75 + VAT.</Li>
            <Li><strong>Tolls:</strong> Salik (Dubai) and Darb (Abu Dhabi) are billed post-rental at the toll amount + 25% admin fee + VAT.</Li>
            <Li><strong>Fines:</strong> traffic fines carry a 10% admin fee. Never pay an authority directly after we've settled it, check with us first to avoid a double charge.</Li>
            <Li><strong>Fuel:</strong> return at the handover level; if lower, we refill and charge the fuel cost plus a 15% admin fee.</Li>
            <Li><strong>Navigation:</strong> we no longer fit GPS units, connect Waze or Google Maps via Apple CarPlay / Android Auto instead.</Li>
          </ul>
        </Section>

        <Section icon={ShieldCheck} title="If something happens: accidents, damage & breakdowns" testId="guide-accidents">
          <ul className="space-y-2 mb-5">
            <Li><strong>Accident steps:</strong> ensure everyone is safe → call the police and obtain a report → don't move the car where the law requires it to stay → call 800 EUROPCAR. File the full report with photos from our <Link to="/report-accident" className="text-[#009900] underline">accident portal</Link>.</Li>
            <Li><strong>Replacement vehicle:</strong> arranged only after the final report is supplied and validated, and the car is released. No replacement while the car is held or impounded by an authority.</Li>
            <Li><strong>Impound:</strong> rental charges continue while the vehicle is held; traffic-fine impounds carry AED 100/day + VAT plus authority and processing fees.</Li>
            <Li><strong>Tyres, rims & windscreens:</strong> puncture repairs are at your cost (call 800 EUROPCAR for help). Rim or windscreen damage is customer-liable without an authority report; with a valid report the normal excess rules apply, AED 0 with SCDW.</Li>
            <Li><strong>Breakdown:</strong> call 800 EUROPCAR, stay safely with the vehicle and share your location. If it can't be fixed on-site we bring a replacement, delivery free, and recover the original. No repairs without our written approval.</Li>
          </ul>
          <ReportColourMatrix />
        </Section>

        <Section icon={MapPinned} title="Minor-accident routing: who to notify by emirate" testId="guide-accident-routing">
          <p className="text-sm text-[#57685e] mb-4">For a minor accident with no injuries, report through the approved channel for the emirate you're in, then call us on 800 EUROPCAR. A final official report is required before insurance can be activated.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[#144733]">
                  <th className="py-2 pr-4 font-semibold w-40">Emirate</th>
                  <th className="py-2 font-semibold">Approved reporting route</th>
                </tr>
              </thead>
              <tbody>
                {ACCIDENT_ROUTING.map(([em, route]) => (
                  <tr key={em} className="border-t border-[#f1f3f1]">
                    <td className="py-3 pr-4 text-xs font-semibold uppercase tracking-wide text-[#144733] align-top">{em}</td>
                    <td className="py-3 text-[#57685e]">{route}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h3 className="heading-upper text-sm text-[#144733] mt-6 mb-3">Information to have ready</h3>
          <ul className="space-y-2">
            {ACCIDENT_INFO.map((info) => <Li key={info}>{info}</Li>)}
          </ul>
        </Section>

        <Section icon={Undo2} title="Returning the car" testId="guide-returns">
          <ul className="space-y-2">
            <Li><strong>Grace period:</strong> a 60-minute grace is available on request only, ask for any extension at least 24 hours before your return time.</Li>
            <Li><strong>Early return on a committed term:</strong> we re-rate to the best rate matching the time you kept the car (e.g. a 9-month booking returned in month 7 is charged at the 6-month rate), no penalty.</Li>
            <Li><strong>22 to 30 days:</strong> one flat monthly rate applies; from day 31 we prorate (monthly ÷ 30 × days).</Li>
            <Li><strong>Cancellation / no-show:</strong> within 24 hours of pickup, AED 105 incl. VAT applies. Earlier cancellations are free.</Li>
            <Li><strong>Abu Dhabi:</strong> we don't pick up or deliver in Abu Dhabi / Al Ain, but you can return at AUH airport or arrange collection there for AED 275 + VAT.</Li>
          </ul>
        </Section>

        <Section icon={Wrench} title="What every rental includes" testId="guide-included">
          <ul className="space-y-2">
            <Li>Comprehensive insurance with CDW · third-party cover over 1 million USD.</Li>
            <Li>24/7 roadside assistance on 800 EUROPCAR · routine maintenance · free replacement vehicle for a genuine breakdown.</Li>
            <Li>Excess limited to AED 1,500 (standard) / AED 2,500 (premium) with a valid police report, zero with SCDW, and zero when the report confirms you were not at fault.</Li>
          </ul>
        </Section>

        <div className="bg-[#f0f9f0] border border-[#009900]/20 rounded-2xl p-6 flex flex-wrap items-center gap-4 justify-between" data-testid="guide-contact-strip">
          <div className="flex items-center gap-3 text-sm text-[#144733]">
            <Phone className="w-5 h-5 text-[#009900]" />
            <span>Reservations <strong>800 EUROPCAR / 800463</strong> · Roadside <strong>800 EUROPCAR</strong> · WhatsApp <strong>800463</strong> · reservations@europcar.com</span>
          </div>
          <Link to="/#book" className="btn-cta rounded px-6 py-3 text-xs" data-testid="guide-book-cta">Start a booking</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
